// The on-screen number pad. On a docked iPad this IS the input.
//
// It exists as its own component because the narrow breakpoint in the prototype
// deleted it — the layout decided a small viewport meant a summary view, and the
// summary view had no way to enter a number. A keypad that can be laid out away
// is not a control, it is a decoration.
//
// The rules it enforces, and nothing else:
//   - one decimal point, and never a leading one that parses as something else;
//   - commit is unavailable until the buffer parses as a number, and the caller
//     may withhold it for its own reasons (samples not all entered, instrument not
//     chosen) by passing `blockedReason`;
//   - the reason a commit is unavailable is always on screen. A greyed button with
//     no explanation is how an operator ends up writing the number on a pad and
//     walking away.
//
// There is NO sign key. Every slice-1 parameter measures a quantity that cannot go
// below zero (weight, moisture, salt, count); a parameter whose band goes negative
// — a freezer temperature, say — needs one added here before it can be captured.
import { opFocus } from '../../shells/OperatorShell';

/** One definition of "parses", used by the pad and by the page that owns it. */
export function parsesAsNumber(text: string): boolean {
  if (!/^\d*\.?\d+$/.test(text.trim())) return false;
  return Number.isFinite(Number(text));
}

export interface KeypadProps {
  /** Raw text of the sample being keyed. Not a number: "1." is a valid state to be in. */
  value: string;
  onChange: (next: string) => void;
  onCommit: () => void;
  /** What committing does, in the operator's words — "Commit moisture", not "Submit". */
  commitLabel: string;
  /** Why commit is unavailable, or null when it is available. Always rendered. */
  blockedReason: string | null;
}

const DIGITS = ['7', '8', '9', '4', '5', '6', '1', '2', '3'];

const KEY_CLASS =
  'min-h-[72px] rounded-(--radius) border border-(--border-strong) bg-(--card) ' +
  'text-(length:--fs-xl) font-semibold text-(--fg) active:bg-(--primary-soft)';

function press(value: string, key: string): string {
  if (key === 'del') return value.slice(0, -1);
  if (key === 'clr') return '';
  // A second point would make the buffer unparseable and the operator would have
  // to work out which of the two to delete.
  if (key === '.' && value.includes('.')) return value;
  // ".5" is ambiguous at a glance beside a limit of 0.5; make the zero explicit.
  if (key === '.' && value === '') return '0.';
  return value + key;
}

export function Keypad({ value, onChange, onCommit, commitLabel, blockedReason }: KeypadProps) {
  const blocked = blockedReason !== null || !parsesAsNumber(value);
  // The pad's own reason only surfaces once the caller has none — the caller's is
  // more specific (which sample, which instrument) and more useful.
  const reason =
    blockedReason ?? (parsesAsNumber(value) ? null : 'Key a number to commit this check.');

  return (
    <div role="group" aria-label="Number pad" className="flex flex-col gap-(--s2)">
      <div className="grid grid-cols-3 gap-(--s2)">
        {DIGITS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onChange(press(value, d))}
            className={`${KEY_CLASS} ${opFocus}`}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(press(value, '.'))}
          aria-label="Decimal point"
          className={`${KEY_CLASS} ${opFocus}`}
        >
          .
        </button>
        <button
          type="button"
          onClick={() => onChange(press(value, '0'))}
          className={`${KEY_CLASS} ${opFocus}`}
        >
          0
        </button>
        <button
          type="button"
          onClick={() => onChange(press(value, 'del'))}
          aria-label="Backspace"
          className={`${KEY_CLASS} ${opFocus}`}
        >
          &#9003;
        </button>
      </div>

      <button
        type="button"
        onClick={() => onChange(press(value, 'clr'))}
        className={`min-h-[56px] rounded-(--radius) border border-(--border) bg-(--subtle) text-(length:--fs-md) font-semibold text-(--muted) ${opFocus}`}
      >
        Clear
      </button>

      <button
        type="button"
        onClick={onCommit}
        disabled={blocked}
        className={`min-h-[72px] rounded-(--radius) border border-(--primary) bg-(--primary) px-(--s4) text-(length:--fs-lg) font-bold text-(--primary-fg) disabled:cursor-not-allowed disabled:border-(--border) disabled:bg-(--subtle) disabled:text-(--muted-2) ${opFocus}`}
      >
        {commitLabel}
      </button>

      {/* A disabled button cannot be focused, so it cannot describe itself. The
          live region is how the reason reaches somebody using a screen reader at
          the moment it changes. */}
      <p
        aria-live="polite"
        className="m-0 min-h-(--s5) text-(length:--fs-sm) font-semibold text-(--muted)"
      >
        {reason ?? ''}
      </p>
    </div>
  );
}

export default Keypad;
